// src/tabs/Beds24SyncPanel.jsx — Phase 6 PR4 (ADR-095, fusion UX onglets)
// Sous-onglet Beds24 du ParamsPanel (admin only).
//
// 4 zones :
// - Sync state : last_poll_at, total_bookings_synced, last_error_*
// - Bookings sans mapping (déduplication property_id+room_id) avec dropdown
//   logements FBZ → bouton "Appliquer le mapping"
// - Mappings actifs (tableau enrichi 8 colonnes : logement + property_id +
//   room_id + mode pricing + dernière sync prix + actif + dernier MAJ + actions)
// - Logements orphelins (V1 KV → V2 missing/inactif unilatéral, BPR4-2)
//
// DA (CHANTIER Paramètres) : habillage SEUL — cartes/accordéons blanc franc +
// pastille teinte-catégorie, pastilles statut sémantiques, tables .fbz-table-wrap
// rhabillées, emoji→lucide. Zéro logique/data/persistance touchée (sync/mapping).
//
// Sécurité : toutes les actions passent par /api/beds24-mapping avec un Bearer
// JWT user. L'endpoint serveur valide ce token et exige role=admin avant
// d'utiliser le service_role.

import { useState, useEffect, useMemo, useRef } from "react";
import {
  Activity, Radio, Inbox, Cable, Unlink, RefreshCw, AlertTriangle,
  CheckCircle2, Info, HelpCircle, GitCompare,
} from "lucide-react";
import { DS } from "../lib/ui";
import { supabase } from "../supabase";
import { clearMappingCache } from "../lib/beds24-mapping-lookup";
import { getAllLogsFlat } from "../lib/sousloc-helpers";
import { usePricingConfigList } from "../lib/use-pricing-config-list";
import PricingConfigEditModal from "../modals/PricingConfigEditModal";
// PR4 commit 8 (NIT-1) : extraction MappingRow + OrphanRow (Beds24SyncPanel ≤ 600 lignes)
import MappingRow from "./beds24/MappingRow";
import OrphanRow from "./beds24/OrphanRow";
// Phase 6.B PR #167c (ADR-101 Q-UI-2 E2) — modal édition min_stay_default Vue 2.
import MinStayDefaultEditModal from "../modals/MinStayDefaultEditModal";
// Phase 6.C PR #167d (ADR-102, D-2 + D-5) — modal bascule mode pricing.
import PricingModeToggleModal from "../modals/PricingModeToggleModal";
import { Modal } from "../components/Modal";
import {
  describeForceResync,
  forceResyncOkDisabled,
} from "../lib/pricing-force-resync-flow";
import { Section, StatusPill, PrimaryButton, SecondaryButton, FicheAlert } from "./_paramsUi";

// MPR4-6 : validation regex côté UI miroir endpoint api/beds24-mapping.js:1165
const LOGEMENT_ID_REGEX = /^[a-z0-9.]+$/i;

// Horizon relu par `pricing_force_resync` : miroir de BEDS24_MAX_HORIZON_DAYS
// (lib/pricing-beds24-reader.js) — le serveur relit [aujourd'hui, +540 j].
const HORIZON_FORCE_RESYNC_JOURS = 540;

/** Dernier jour de l'horizon, en ISO. Arithmétique UTC, comme le serveur. */
function horizonFinIso(debutIso, jours) {
  const d = new Date(`${debutIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + jours);
  return d.toISOString().slice(0, 10);
}

// ADJ3 — Monitoring : on sépare DEUX choses distinctes que l'ancien
// evaluateWebhookHealth confondait (il marquait « Muet » au-delà de 24 h) :
//   (1) la SANTÉ DE LA CHAÎNE — un event a-t-il déjà transité ? Ne se dégrade
//       PAS avec le temps : un prix inchangé depuis des jours n'est pas une
//       panne. 'connected' (au moins un event reçu un jour) / 'never' (jamais
//       d'event — à vérifier) / 'unknown' (lecture serveur indisponible).
//   (2) le DERNIER CHANGEMENT DE PRIX reçu — info événementielle neutre
//       (date + type + âge), sans couleur d'alerte.

/**
 * Évalue la SANTÉ DE LA CHAÎNE webhook Beds24 temps réel. Pur (testable
 * isolément). Indépendant de l'horloge : la chaîne ne « tombe » pas parce
 * qu'aucun prix n'a changé récemment.
 *
 * @param {object|null} webhookStatus — { last_event_at, event_type,
 *   processing_duration_ms } renvoyé par handleList, ou null si indisponible.
 * @returns {{ kind: 'connected'|'never'|'unknown' }}
 */
export function evaluateWebhookChain(webhookStatus) {
  // null = lecture serveur indisponible (best-effort).
  if (webhookStatus === null || webhookStatus === undefined) {
    return { kind: "unknown" };
  }
  // Pas de last_event_at = aucun event jamais reçu → chaîne à vérifier.
  if (!webhookStatus.last_event_at) {
    return { kind: "never" };
  }
  const ts = new Date(webhookStatus.last_event_at).getTime();
  // Date non parsable = on ne peut pas conclure → indisponible.
  if (Number.isNaN(ts)) {
    return { kind: "unknown" };
  }
  // Au moins un event a transité un jour → la chaîne fonctionne.
  return { kind: "connected" };
}

/**
 * Extrait l'info DERNIER CHANGEMENT DE PRIX reçu via webhook. Neutre (pas
 * d'alerte). `nowMs` injectable pour les tests (calcul de l'âge relatif).
 *
 * @param {object|null} webhookStatus — cf. evaluateWebhookChain.
 * @param {number} [nowMs] — horloge courante (default Date.now()).
 * @returns {{ hasEvent: boolean, ageMs: number|null, lastEventAt: string|null,
 *   eventType: string|null, processingMs: number|null }}
 */
export function lastPriceEvent(webhookStatus, nowMs = Date.now()) {
  const empty = {
    hasEvent: false,
    ageMs: null,
    lastEventAt: null,
    eventType: null,
    processingMs: null,
  };
  if (!webhookStatus || !webhookStatus.last_event_at) return empty;
  const ts = new Date(webhookStatus.last_event_at).getTime();
  if (Number.isNaN(ts)) return empty;
  return {
    hasEvent: true,
    ageMs: nowMs - ts,
    lastEventAt: webhookStatus.last_event_at,
    eventType: webhookStatus.event_type || null,
    processingMs:
      typeof webhookStatus.processing_duration_ms === "number"
        ? webhookStatus.processing_duration_ms
        : null,
  };
}

/**
 * Formate une durée (ms) en libellé court relatif français (« il y a … »).
 * @param {number|null} ageMs
 * @returns {string}
 */
export function formatRelativeAge(ageMs) {
  if (ageMs == null || ageMs < 0) return "—";
  const sec = Math.floor(ageMs / 1000);
  if (sec < 60) return `il y a ${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return `il y a ${d} j`;
}

export default function Beds24SyncPanel({ cfg }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncState, setSyncState] = useState(null);
  const [bookingsNoMapping, setBookingsNoMapping] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [selection, setSelection] = useState({}); // key "propId|roomId" → logement_id sélectionné
  const [pending, setPending] = useState({}); // key → bool (POST en cours)
  const [toast, setToast] = useState(null); // { kind, msg }
  // BUG 62 — état webhook temps réel (dernier event Inventory reçu, cf. handleList).
  const [webhookStatus, setWebhookStatus] = useState(null);

  // Phase 6 PR4 — pricing config list + modal édition + freshness map
  const {
    configs,
    dryRunActive,
    manualCounts,
    manualCountsEtat,
    manualCountsWindow,
    refetch: refetchConfigs,
    upsert,
    remove,
  } = usePricingConfigList();
  const [editing, setEditing] = useState(null); // null | { existing?, logement_id_default? }
  const [forcingResyncId, setForcingResyncId] = useState(null);
  // Garde « Force resync » (2026-09-28) — logement_id dont la garde est
  // ouverte, ou null. Le geste ne part plus au premier clic : il écrase des
  // prix fixés à la main et RIEN ne conserve les anciennes valeurs.
  const [forceResyncDemande, setForceResyncDemande] = useState(null);
  const [freshnessByLogement, setFreshnessByLogement] = useState({});
  // Phase 6.B PR #167c — modal édition min_stay_default ouverte pour un logement.
  const [editingMinStay, setEditingMinStay] = useState(null); // null | { logementId, logementLabel }
  // Phase 6.C PR #167d (ADR-102, D-2 + D-5) — modal bascule mode pricing.
  // togglingConfig contient le row config en cours de bascule (existing) OU null.
  const [togglingConfig, setTogglingConfig] = useState(null);
  // Phase 6.C PR #167d (LPRd-4) — id du logement en cours de bascule, pour
  // disable row pendant l'appel PATCH (UI feedback).
  const [togglingBusyId, setTogglingBusyId] = useState(null);


  // R3 (ADR-256) — divergences Beds24 (lecture seule). Fetché au mount.
  const [reconLoading, setReconLoading] = useState(true);
  const [reconError, setReconError] = useState(null);
  const [reconData, setReconData] = useState(null); // { threshold, counts, byLogement }

  // Ref vers la zone "Bookings sans mapping" pour scroll depuis orphelins
  const bookingsRef = useRef(null);

  // Tous les logements FBZ INCLUANT les chambres sous-loc (lc1, lc2, lc3) —
  // chaque chambre peut avoir son propre mapping Beds24 distinct (audit
  // F15 résolu via getAllLogsFlat ADR-051 qui dédup par id et attache parentLog).
  const allLogs = useMemo(
    () =>
      getAllLogsFlat(cfg).map((l) => ({
        id: l.id,
        nom: l.nom_complet || l.nom,
        prop: l.propNom,
      })),
    [cfg],
  );

  // MPR4-6 : warning console si logement_id non-conforme regex (silencieux côté UX)
  useEffect(() => {
    for (const l of allLogs) {
      if (l?.id && !LOGEMENT_ID_REGEX.test(l.id)) {
        // eslint-disable-next-line no-console
        console.warn(
          "[Beds24SyncPanel] logement_id non conforme regex /^[a-z0-9.]+$/i :",
          l.id,
        );
      }
    }
  }, [allLogs]);

  // Index configs par logement_id (jointure rapide tableau Mappings actifs)
  const configsById = useMemo(() => {
    const m = new Map();
    for (const c of configs) m.set(c.logement_id, c);
    return m;
  }, [configs]);

  // Index mappings (actifs + inactifs) par logement_id pour distinguer
  // cas 1 (aucun mapping) vs cas 2 (mapping inactif) en zone orphelins (BPR4-2).
  const mappingsById = useMemo(() => {
    const m = new Map();
    for (const x of mappings) m.set(x.logement_id, x);
    return m;
  }, [mappings]);

  // Phase 6 PR4 (BPR4-1 + BPR4-2) — Logements orphelins V1 KV → V2 missing/inactif.
  //
  // Sémantique UNILATÉRALE (BPR4-2) : la zone "Logements orphelins"
  // détecte les logements présents dans V1 KV (cfg.props via getAllLogsFlat)
  // qui n'ont PAS de mapping Beds24 actif côté V2 (table
  // logements_beds24_mapping). Trois cas couverts :
  //   1. Logement V1 + aucun mapping V2          → "Aucun mapping"
  //   2. Logement V1 + mapping V2 inactif         → "Mapping désactivé"
  //   3. Logement V1 renommé sans MAJ V2          → PAS ici, géré dans
  //                                                  tableau Mappings actifs avec
  //                                                  badge rouge "— orphelin"
  //
  // BPR4-1 : filter mappings.filter(m => m.active) — un mapping inactif
  // est considéré comme orphelin (besoin de re-mapping intentionnel).
  const orphanLogs = useMemo(() => {
    const activeMappedIds = new Set(
      mappings.filter((m) => m.active).map((m) => m.logement_id),
    );
    return allLogs
      .filter((l) => !activeMappedIds.has(l.id))
      .map((l) => ({
        ...l,
        mapping: mappingsById.get(l.id) || null, // mapping inactif éventuel
        config: configsById.get(l.id) || null,
      }));
  }, [allLogs, mappings, mappingsById, configsById]);

  function scrollToBookingsNoMapping() {
    if (bookingsRef.current) {
      bookingsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  async function callApi(method, body) {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token)
      throw new Error("Session expirée — recharge la page.");
    const init = {
      method,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        "Content-Type": "application/json",
      },
    };
    if (body !== undefined) init.body = JSON.stringify(body);
    const res = await fetch("/api/beds24-mapping", init);
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.error || `Erreur HTTP ${res.status}`);
    return json;
  }

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const data = await callApi("GET");
      setSyncState(data.sync_state);
      setBookingsNoMapping(data.bookings_no_mapping || []);
      setMappings(data.mappings || []);
      setWebhookStatus(data.webhook_status || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    refresh();
  }, []);

  // MPR4-4 : fetch freshness avec AbortController + cleanup (pattern miroir
  // use-daily-pricing.js:89). BPR4-1 : filter active mappings uniquement.
  useEffect(() => {
    const activeIds = mappings
      .filter((m) => m.active)
      .map((m) => m.logement_id)
      .filter(Boolean);
    if (activeIds.length === 0) {
      setFreshnessByLogement({});
      return;
    }
    const controller = new AbortController();
    const from = new Date().toISOString().slice(0, 10);
    const to = new Date(Date.now() + 30 * 86400 * 1000)
      .toISOString()
      .slice(0, 10);
    (async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const qs = new URLSearchParams({
          action: "pricing_calendar_batch_read",
          logement_ids: activeIds.join(","),
          from,
          to,
        });
        const res = await fetch(`/api/beds24-mapping?${qs.toString()}`, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (controller.signal.aborted) return;
        if (!res.ok) return;
        const json = await res.json();
        if (controller.signal.aborted) return;
        const map = {};
        for (const l of json.logements || []) map[l.logement_id] = l;
        setFreshnessByLogement(map);
      } catch (e) {
        if (e.name !== "AbortError") {
          // eslint-disable-next-line no-console
          console.error("[Beds24SyncPanel] freshness fetch error", e);
        }
      }
    })();
    return () => controller.abort();
  }, [mappings]);

  // R3 (ADR-256) — fetch divergences Beds24 au mount (lecture seule, 1 appel).
  // Pattern miroir de la freshness ci-dessus (AbortController + cleanup).
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setReconLoading(true);
      setReconError(null);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) {
          if (!controller.signal.aborted) {
            setReconError("Session absente.");
            setReconLoading(false);
          }
          return;
        }
        const res = await fetch(
          "/api/beds24-mapping?action=beds24_reconciliation_status",
          {
            signal: controller.signal,
            headers: { Authorization: `Bearer ${session.access_token}` },
          },
        );
        if (controller.signal.aborted) return;
        if (!res.ok) {
          setReconError(`Erreur serveur (${res.status}).`);
          setReconLoading(false);
          return;
        }
        const json = await res.json();
        if (controller.signal.aborted) return;
        setReconData(json);
        setReconLoading(false);
      } catch (e) {
        if (e.name === "AbortError") return;
        // eslint-disable-next-line no-console
        console.error("[Beds24SyncPanel] recon fetch error", e);
        setReconError("Chargement impossible.");
        setReconLoading(false);
      }
    })();
    return () => controller.abort();
  }, []);

  function flashToast(kind, msg, durationMs = 5000) {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), durationMs);
  }

  // MPR4-5 : portage complet 3 cas seed (depuis PricingConfigPanel:52-85)
  function handleSavedToast(result) {
    if (result.ok) {
      if (result.seed) {
        // Cas 1 : seed réussi + jours synchronisés
        if (result.seed.success && result.seed.count > 0) {
          flashToast(
            "ok",
            `Configuration sauvegardée. ${result.seed.count} prix synchronisés depuis Beds24.`,
            6000,
          );
        }
        // Cas 2 : seed réussi mais aucun prix Beds24
        else if (result.seed.success && result.seed.count === 0) {
          flashToast(
            "info",
            "Configuration sauvegardée. Aucun prix existant dans Beds24 (calendrier vide).",
            6000,
          );
        }
        // Cas 3 : seed échoué
        else {
          flashToast(
            "info",
            "Configuration sauvegardée. Seed Beds24 échoué — utiliser Force resync pour réessayer.",
            6000,
          );
        }
      } else {
        flashToast("ok", "Configuration sauvegardée.");
      }
    } else if (result.conflict) {
      flashToast("err", "Config modifiée par un autre admin, rechargée.");
    } else {
      flashToast("err", `Erreur : ${result.error}`);
    }
    // MPR4-2 : refresh combiné mappings + configs + invalidation freshness
    refresh();
    refetchConfigs();
    setFreshnessByLogement({});
  }

  async function applyMapping(propertyId, roomId) {
    const key = `${propertyId}|${roomId ?? ""}`;
    const logement_id = selection[key];
    if (!logement_id) {
      flashToast("err", "Sélectionne un logement avant d'appliquer.");
      return;
    }
    setPending((prev) => ({ ...prev, [key]: true }));
    try {
      await callApi("POST", {
        logement_id,
        beds24_property_id: propertyId,
        beds24_room_id: roomId,
      });
      flashToast(
        "ok",
        `Mapping ${logement_id} ← (${propertyId}/${roomId ?? "∅"}) enregistré. Le prochain run du cron Beds24 (≤ 15 min) créera les résas.`,
      );
      setSelection((prev) => {
        const x = { ...prev };
        delete x[key];
        return x;
      });
      clearMappingCache(logement_id);
      await refresh();
    } catch (e) {
      flashToast("err", e.message);
    } finally {
      setPending((prev) => ({ ...prev, [key]: false }));
    }
  }

  async function handleDelete(logement_id) {
    if (
      !window.confirm(
        `Désactiver la config pricing de ${logement_id} ? (soft-delete, réversible)`,
      )
    )
      return;
    const result = await remove(logement_id);
    if (result.ok) {
      flashToast("ok", `${logement_id} désactivé.`);
      setFreshnessByLogement({});
    } else {
      flashToast("err", `Erreur : ${result.error}`);
    }
  }

  async function handleForceResync(logement_id) {
    // Le clic OUVRE la garde. Rien n'est écrit tant que Dimitri n'a pas lu ce
    // que le geste va écraser (cf. `describeForceResync`).
    setForceResyncDemande(logement_id);
  }

  async function lancerForceResync(logement_id) {
    setForceResyncDemande(null);
    setForcingResyncId(logement_id);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        flashToast("err", "Non authentifié");
        return;
      }
      const res = await fetch(
        "/api/beds24-mapping?action=pricing_force_resync",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ logement_id }),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (res.status === 429) {
        flashToast(
          "info",
          `Limite atteinte (5 min). Réessayer dans ${json.retry_after_seconds || "?"}s.`,
        );
      } else if (!res.ok) {
        flashToast("err", `Erreur : ${json.error || `http_${res.status}`}`);
      } else {
        // LA PROMESSE FAITE AVANT DOIT POUVOIR ÊTRE INFIRMÉE APRÈS. La garde
        // annonce « tu pourras les relire dans le journal d'audit » AVANT
        // d'écraser. Deux façons pour ce relevé de manquer, toutes deux APRÈS
        // un écrasement déjà fait :
        //   · `prix_epingles_avant_releve: false` — la lecture d'avant a
        //     échoué, ou a saturé son plafond ;
        //   · pas d'`audit_log_id` — `pricingAuditLog` est best-effort
        //     (try/catch) : le relevé existait, la ligne qui le porte non.
        // Dans les deux cas le geste a RÉUSSI, mais le filet promis n'est pas
        // là : on le dit, au lieu d'un « OK » qui laisserait croire l'inverse.
        const releveManquant =
          json.prix_epingles_avant_releve === false || !json.audit_log_id;
        const retirees = json.epingles_retirees;
        if (releveManquant) {
          flashToast(
            "err",
            `Resync fait : ${json.items_upserted || 0} jours. ⚠️ Les prix épinglés écrasés n'ont PAS pu être relevés dans le journal — ils ne sont retrouvables nulle part.`,
            12000,
          );
        } else {
          flashToast(
            "ok",
            typeof retirees === "number" && retirees > 0
              ? `Resync OK : ${json.items_upserted || 0} jours. ${retirees} épingle${retirees > 1 ? "s" : ""} 📌 retirée${retirees > 1 ? "s" : ""} — les prix d'avant sont dans le journal d'audit.`
              : `Resync OK : ${json.items_upserted || 0} jours.`,
            8000,
          );
        }
        refetchConfigs();
        setFreshnessByLogement({}); // re-trigger fetch freshness
      }
    } catch (e) {
      flashToast("err", `Erreur : ${e.message}`);
    } finally {
      setForcingResyncId(null);
    }
  }

  // ── LES COMPTES DE DATES MANUELLES VIENNENT DU SERVEUR ───────────────────
  //
  // RÈGLE : un `count` venu d'une table que le client ne peut pas lire n'est
  // jamais un fait.
  //
  // Ce qui était ici avant : DEUX comptages en Supabase direct sur
  // `daily_pricing`, depuis le navigateur (rôle `authenticated`). La table est
  // sous RLS avec UNE SEULE policy (`service_role_all`) et un GRANT SELECT qui
  // existe pour `authenticated` — combinaison qui ne rend PAS 42501 : PostgREST
  // rend tranquillement 0 ligne et `count = 0`. Aucun garde ne se déclenche,
  // et l'écran affiche « aucune date manuelle » avec l'aplomb d'un fait.
  //
  // Ce n'est pas une hypothèse : le compteur `manual_push_set` (arrivé avec
  // PR #167d, commentaire « RLS Pattern A permet SELECT admin sur
  // daily_pricing » — faux) comptait 0 depuis toujours, alors que la base
  // porte 66 lignes. Son alerte « > 50 dates manuelles » n'a JAMAIS pu
  // s'afficher. Et la garde « Force resync » aurait annoncé « aucun prix fixé
  // à la main ne sera perdu » juste avant d'en effacer douze.
  //
  // ⚠️ Corriger le chemin ne fera pas crier l'alerte pour autant, et c'est
  // normal : au 2026-09-28, lc3 porte 41 épingles et lc1 29, mais TOUTES
  // datées du 2026-09-05 au plus tard — donc dans le PASSÉ. Sur la fenêtre à
  // venir, c'est 0 partout, sous le seuil de 50. Le critère est bon, il ne se
  // déclenche simplement pas. Un « 0 » ici est désormais un zéro MESURÉ.
  //
  // Désormais : `pricing_logement_config` (requireAdmin, service_role) rend
  // `manual_counts` avec la même liste de configs — donc `refetchConfigs()`
  // après un resync rafraîchit AUSSI les comptes, et la garde rouverte ne lit
  // plus un chiffre périmé.
  //
  // Le calcul se fait AU RENDU (useMemo), jamais au chargement, et l'absence
  // n'est pas zéro. `manualCounts === null` se lit avec `manualCountsEtat` :
  // 'chargement' tant que la réponse n'est pas revenue, 'indisponible' quand
  // le serveur a répondu sans pouvoir compter (sinon on ferait tourner un
  // spinner à vie sur une cause qui ne se résoudra pas). Logement absent de la
  // carte ⇒ 'indisponible'
  // (config inconnue du serveur : on ne devine pas) ; compteur `null` pour ce
  // logement ⇒ 'indisponible'.
  const epinglesParLogement = useMemo(() => {
    const out = {};
    const ids = new Set([
      ...mappings.map((mp) => mp?.logement_id).filter(Boolean),
      ...Object.keys(manualCounts || {}),
    ]);
    for (const id of ids) {
      if (manualCounts === null) {
        // « je compte » ≠ « je n'ai pas pu compter » : un serveur incapable
        // rendu comme un chargement en cours, c'est un spinner à vie sur une
        // cause qui ne se résoudra pas.
        out[id] = {
          etat:
            manualCountsEtat === "chargement" ? "chargement" : "indisponible",
          nombre: null,
        };
        continue;
      }
      const n = manualCounts[id]?.epingles;
      out[id] =
        typeof n === "number"
          ? { etat: "nombre", nombre: n }
          : { etat: "indisponible", nombre: null };
    }
    return out;
  }, [mappings, manualCounts, manualCountsEtat]);

  // Compteur frère (bascule de mode) — même source serveur. `null` = inconnu,
  // et les deux consommateurs (MappingRow, PricingModeToggleModal) testent
  // déjà `typeof === 'number'` : un compte inconnu n'affiche aucune alerte
  // plutôt qu'une alerte fausse.
  const manualPushSetCountByLogement = useMemo(() => {
    const out = {};
    for (const [id, v] of Object.entries(manualCounts || {})) {
      out[id] = typeof v?.manualPushSet === "number" ? v.manualPushSet : null;
    }
    return out;
  }, [manualCounts]);

  // PR #167d (ADR-102, D-2) — handler bascule mode pricing depuis
  // PricingModeToggleModal. Soumet le PATCH via use-pricing-config-list.upsert
  // qui gère le If-Match + audit côté serveur.
  async function handleToggleModeSubmit(payload, opts) {
    const logId = payload?.logement_id;
    if (!logId) return { ok: false, error: "no_logement_id" };
    setTogglingBusyId(logId);
    try {
      const result = await upsert(payload, opts);
      if (result.ok) {
        const fromMode =
          togglingConfig?.pricing_mode === "manual" ? "Manual" : "PriceLabs";
        const toMode =
          payload.pricing_mode === "manual" ? "Manual" : "PriceLabs";
        flashToast("ok", `Bascule ${logId} : ${fromMode} → ${toMode}`, 5000);
        // Invalide freshness + refetch configs (refetch déjà fait par upsert)
        setFreshnessByLogement({});
        // Reset count manual_push_set pour ce logement (sera recalculé sur
        // prochain effect mappings → mais on déclenche immédiat avec refresh).
        refresh();
      } else if (result.conflict) {
        flashToast("err", "Config modifiée par un autre admin, rechargée.");
      } else {
        flashToast("err", `Erreur bascule : ${result.error}`);
      }
      return result;
    } finally {
      setTogglingBusyId(null);
    }
  }

  // BPR4-1 : un mapping inactif est traité côté ligne (badge + bouton
  // Réactiver). Réactivation = upsert mapping avec active=true via POST
  // standard (l'endpoint /api/beds24-mapping fait un UPSERT et active=true par
  // défaut côté server).
  async function handleReactivateMapping(m) {
    try {
      await callApi("POST", {
        logement_id: m.logement_id,
        beds24_property_id: m.beds24_property_id,
        beds24_room_id: m.beds24_room_id,
      });
      flashToast("ok", `Mapping ${m.logement_id} réactivé.`);
      clearMappingCache(m.logement_id);
      await refresh();
    } catch (e) {
      flashToast("err", `Erreur : ${e.message}`);
    }
  }

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  };

  // Toast — fond tinté sémantique + icône lucide.
  const toastTone =
    toast?.kind === "ok" ? "success" : toast?.kind === "info" ? "info" : "danger";
  const toastBg =
    toastTone === "success" ? DS.successBg : toastTone === "info" ? DS.cielSoft : DS.dangerBg;
  const toastColor =
    toastTone === "success" ? DS.successText : toastTone === "info" ? DS.marineDeep : DS.dangerText;
  const toastBorder =
    toastTone === "success" ? "#bfe6d6" : toastTone === "info" ? "#c8e0f5" : "#f3c6c6";
  const ToastIcon =
    toastTone === "success" ? CheckCircle2 : toastTone === "info" ? Info : AlertTriangle;

  return (
    <div style={{ fontFamily: DS.fontBody }}>
      {/* Toast */}
      {toast && (
        <div
          role="alert"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            display: "flex",
            alignItems: "flex-start",
            gap: 9,
            padding: "10px 14px",
            borderRadius: 10,
            marginBottom: 12,
            background: toastBg,
            color: toastColor,
            borderLeft: `3px solid ${toastBorder}`,
            border: `0.5px solid ${toastBorder}`,
            fontSize: 12.5,
            fontWeight: 600,
          }}
        >
          <ToastIcon size={15} strokeWidth={1.9} style={{ flex: "0 0 auto", marginTop: 1 }} />
          <span style={{ flex: 1 }}>{toast.msg}</span>
        </div>
      )}

      {/* État synchro Beds24 */}
      <Section
        tint="ciel"
        icon={Activity}
        title="État synchro Beds24"
        subtitle="Filet de rattrapage cron : sync résas (15 min) + pull prix (4 h, lecture seule)."
        headerRight={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* v3 ÉTAPE 1 — libellé sans jargon ambigu. `dryRunActive` reflète
                PRICING_PUSH_DRY_RUN : true = écritures Beds24 SIMULÉES (garde
                de sécurité) ; false = écritures Beds24 RÉELLES (LIVE). L'ancien
                « DRY_RUN OFF » rouge se lisait à tort « sync désactivée ». LIVE
                est l'état normal actif → ton success (vert), pas une alerte. */}
            <StatusPill
              tone={dryRunActive ? "info" : "success"}
              title={
                dryRunActive
                  ? "PRICING_PUSH_DRY_RUN=true — les écritures vers Beds24 sont simulées (aucun push réel)."
                  : "PRICING_PUSH_DRY_RUN=false — les écritures vers Beds24 sont réelles."
              }
            >
              {dryRunActive ? "Écritures simulées (DRY_RUN)" : "Écritures Beds24 LIVE"}
            </StatusPill>
            <SecondaryButton disabled={loading} onClick={refresh} style={{ padding: "8px 14px", fontSize: 12 }}>
              <RefreshCw size={14} strokeWidth={1.9} /> {loading ? "…" : "Rafraîchir"}
            </SecondaryButton>
          </div>
        }
      >
        {error && (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 9,
              padding: "10px 12px",
              background: DS.dangerBg,
              color: DS.dangerText,
              borderRadius: 8,
              borderLeft: `3px solid ${DS.danger}`,
              fontSize: 12.5,
              marginBottom: 10,
            }}
          >
            <AlertTriangle size={15} strokeWidth={1.9} style={{ flex: "0 0 auto", marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}
        {!error && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
              gap: 10,
            }}
          >
            <Stat
              label="Dernier passage cron (filet)"
              value={fmtDate(syncState?.last_poll_at)}
            />
            <Stat
              label="Dernier modif vu"
              value={fmtDate(syncState?.last_modified_seen)}
            />
            <Stat
              label="Total bookings synced"
              value={syncState?.total_bookings_synced ?? 0}
              color={DS.marine}
            />
            <Stat
              label="Dernière erreur"
              value={
                syncState?.last_error_message
                  ? fmtDate(syncState?.last_error_at)
                  : "—"
              }
              color={syncState?.last_error_message ? DS.danger : DS.textPrimary}
            />
          </div>
        )}
        {syncState?.last_error_message && (
          <div
            style={{
              marginTop: 10,
              padding: "8px 12px",
              background: DS.dangerBg,
              color: DS.dangerText,
              borderRadius: 8,
              fontSize: 11,
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
            }}
          >
            {syncState.last_error_message}
          </div>
        )}
        <div
          style={{
            marginTop: 12,
            fontSize: 11,
            color: DS.textMuted,
            lineHeight: 1.5,
          }}
        >
          {/* Fréquences reprises des workflows GitHub Actions, seule source de
              vérité : .github/workflows/beds24-sync.yml (cron 15 min) et
              .github/workflows/pricing-sync.yml (cron 30 min depuis le
              2026-07-03 — le texte annonçait encore les 4 h d'avant ce
              changement). */}
          Les résas arrivent en <strong>temps réel</strong> via le webhook
          Beds24 (≈ 3 s, voir encart ci-dessous). Le cron ci-dessus est un{" "}
          <strong>filet de rattrapage</strong> : sync résas toutes les 15 min,
          pull prix toutes les 30 min (lecture seule, zéro push). Pour forcer un
          passage immédiat : onglet <strong>Actions</strong> du repo GitHub →
          workflow <em>Beds24 Sync Cron</em> → bouton « Run workflow ».
        </div>
      </Section>

      {/* Webhook temps réel (BUG 62) — dernier event Inventory reçu */}
      <WebhookStatusCard webhookStatus={webhookStatus} fmtDate={fmtDate} />

      {/* Divergences Beds24 (R3, ADR-256) — diagnostic LECTURE SEULE */}
      <Beds24ReconciliationSection
        loading={reconLoading}
        error={reconError}
        data={reconData}
        allLogs={allLogs}
      />

      {/* Bookings sans mapping */}
      <div ref={bookingsRef}>
        <Section
          tint="ardoise"
          icon={Inbox}
          title={`Bookings sans mapping (${bookingsNoMapping.length})`}
          subtitle="Couples (property_id + room_id) reçus de Beds24 sans logement FBZ associé."
        >
          {bookingsNoMapping.length === 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                padding: "12px 14px",
                background: DS.successBg,
                color: DS.successText,
                borderRadius: 10,
                borderLeft: `3px solid ${DS.success}`,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <CheckCircle2 size={16} strokeWidth={1.9} style={{ flex: "0 0 auto" }} />
              <span>
                Aucun booking en attente de mapping — tous les bookings reçus
                sont rattachés à un logement.
              </span>
            </div>
          )}
          {bookingsNoMapping.map((b) => {
            const key = `${b.beds24_property_id}|${b.beds24_room_id ?? ""}`;
            return (
              <div
                key={key}
                style={{
                  border: `0.5px solid ${DS.border}`,
                  borderRadius: 10,
                  padding: 12,
                  marginBottom: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: "1 1 280px", minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: DS.marine,
                      marginBottom: 2,
                    }}
                  >
                    property{" "}
                    <code
                      style={{
                        background: DS.cielSoft,
                        padding: "1px 6px",
                        borderRadius: 4,
                        fontSize: 11,
                      }}
                    >
                      {b.beds24_property_id}
                    </code>
                    {b.beds24_room_id !== null &&
                      b.beds24_room_id !== undefined && (
                        <>
                          {" "}
                          · room{" "}
                          <code
                            style={{
                              background: DS.cielSoft,
                              padding: "1px 6px",
                              borderRadius: 4,
                              fontSize: 11,
                            }}
                          >
                            {b.beds24_room_id}
                          </code>
                        </>
                      )}
                  </div>
                  <div style={{ fontSize: 10, color: DS.textMuted }}>
                    {b.nb_bookings} booking{b.nb_bookings > 1 ? "s" : ""} en
                    attente · vu pour la dernière fois {fmtDate(b.last_seen)}
                  </div>
                </div>
                <select
                  value={selection[key] || ""}
                  onChange={(e) =>
                    setSelection((prev) => ({ ...prev, [key]: e.target.value }))
                  }
                  style={{
                    border: `0.5px solid ${DS.borderHover}`,
                    borderRadius: DS.radiusControl,
                    padding: "8px 10px",
                    fontSize: 12,
                    outline: "none",
                    background: DS.card,
                    fontFamily: DS.fontBody,
                    minWidth: 200,
                  }}
                >
                  <option value="">— Choisir un logement —</option>
                  {allLogs.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.nom} ({l.prop} · {l.id})
                    </option>
                  ))}
                </select>
                <PrimaryButton
                  onClick={() =>
                    applyMapping(b.beds24_property_id, b.beds24_room_id)
                  }
                  disabled={!selection[key] || pending[key]}
                  style={{ padding: "9px 14px", fontSize: 12, whiteSpace: "nowrap" }}
                >
                  {pending[key] ? "…" : "Appliquer"}
                </PrimaryButton>
              </div>
            );
          })}
          {allLogs.length === 0 && bookingsNoMapping.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 9,
                padding: "10px 12px",
                background: DS.warningBg,
                color: DS.warningText,
                borderRadius: 8,
                borderLeft: `3px solid ${DS.warning}`,
                fontSize: 11,
                marginTop: 6,
              }}
            >
              <AlertTriangle size={14} strokeWidth={1.9} style={{ flex: "0 0 auto", marginTop: 1 }} />
              <span>
                Aucun logement FBZ déclaré dans la config. Ajoute un logement
                dans l'onglet Général.
              </span>
            </div>
          )}
        </Section>
      </div>

      {/* Mappings actifs — tableau enrichi PR4 (8 colonnes) */}
      <Section
        tint="ardoise"
        icon={Cable}
        title={`Mappings actifs (${mappings.length})`}
        subtitle="Couples Beds24 → logement FBZ, enrichis du mode pricing + dernière sync prix."
      >
        {mappings.length === 0 && (
          <div style={{ fontSize: 12, color: DS.textMuted }}>
            Aucun mapping configuré.
          </div>
        )}
        {mappings.length > 0 && (
          <div className="fbz-table-wrap" style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead>
                <tr style={{ background: DS.pageBg }}>
                  <th style={th}>Logement FBZ</th>
                  <th style={th}>property_id</th>
                  <th style={th}>room_id</th>
                  <th style={th}>Mode pricing</th>
                  <th style={th}>Dernière sync prix</th>
                  {/* PR #167c (ADR-101 Q-UI-2) — col 9 min nuits défaut Niveau 1 */}
                  <th style={th}>Min nuits défaut</th>
                  <th style={th}>Actif</th>
                  <th style={th}>Dernier MAJ</th>
                  <th style={{ ...th, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((m) => {
                  const log = allLogs.find((l) => l.id === m.logement_id);
                  const config = configsById.get(m.logement_id) || null;
                  const freshness = freshnessByLogement[m.logement_id] || null;
                  return (
                    <MappingRow
                      key={`${m.logement_id}-${m.beds24_property_id}-${m.beds24_room_id ?? "∅"}`}
                      mapping={m}
                      log={log}
                      config={config}
                      freshness={freshness}
                      forcingResyncId={forcingResyncId}
                      fmtDate={fmtDate}
                      onEdit={setEditing}
                      onForceResync={handleForceResync}
                      onDelete={handleDelete}
                      onReactivateMapping={handleReactivateMapping}
                      onEditMinStay={(logementId) => {
                        // PR #167c (ADR-101 Q-UI-2 E2) — ouvre modal édition min_stay
                        const lg = allLogs.find((x) => x.id === logementId);
                        setEditingMinStay({
                          logementId,
                          logementLabel: lg
                            ? `${lg.nom} (${lg.prop})`
                            : logementId,
                        });
                      }}
                      onToggleMode={(cfg) => setTogglingConfig(cfg)}
                      toggleModeBusy={togglingBusyId === m.logement_id}
                      otherModalOpen={
                        !!editing ||
                        !!editingMinStay ||
                        !!togglingConfig ||
                        !!forceResyncDemande
                      }
                      manualPushSetCount={
                        manualPushSetCountByLogement[m.logement_id] ?? null
                      }
                      styles={{ th, td, btnSecondary }}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* ───────────────────────────────────────────────────────────────
          Logements orphelins (Phase 6 PR4 §4 — BPR4-1 + BPR4-2)

          Sémantique UNILATÉRALE V1 KV → V2 missing/inactif :
          - Détecte logements V1 (cfg.props via getAllLogsFlat) qui n'ont
            pas de mapping Beds24 actif côté V2.
          - Sens inverse (V2 sans logement V1) reste géré dans tableau
            Mappings actifs ci-dessus avec badge rouge "— orphelin".
          - BPR4-1 : mapping `active=false` apparaît ici comme "Mapping
            désactivé" pour permettre re-mapping intentionnel.
          ─────────────────────────────────────────────────────────────── */}
      <Section
        tint="ardoise"
        icon={Unlink}
        title={`Logements sans mapping Beds24 (${orphanLogs.length})`}
        subtitle="Logements FBZ déclarés (V1) sans mapping Beds24 actif côté base (V2)."
      >
        {orphanLogs.length === 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              padding: "12px 14px",
              background: DS.successBg,
              color: DS.successText,
              borderRadius: 10,
              borderLeft: `3px solid ${DS.success}`,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <CheckCircle2 size={16} strokeWidth={1.9} style={{ flex: "0 0 auto" }} />
            <span>
              Aucun logement orphelin — tous les logements FBZ ont un mapping
              Beds24 actif.
            </span>
          </div>
        )}
        {orphanLogs.length > 0 && (
          <div className="fbz-table-wrap" style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead>
                <tr style={{ background: DS.pageBg }}>
                  <th style={th}>Logement FBZ</th>
                  <th style={th}>ID</th>
                  <th style={th}>Mode pricing</th>
                  <th style={th}>Statut</th>
                  <th style={{ ...th, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orphanLogs.map((l) => (
                  <OrphanRow
                    key={l.id}
                    log={l}
                    bookingsNoMappingCount={bookingsNoMapping.length}
                    fmtDate={fmtDate}
                    onEdit={setEditing}
                    onReactivateMapping={handleReactivateMapping}
                    onScrollToBookings={scrollToBookingsNoMapping}
                    onFlashToast={flashToast}
                    styles={{ th, td, btnSecondary }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Modal édition pricing (Phase 6 PR4) */}
      {editing && (
        <PricingConfigEditModal
          existing={editing.existing || null}
          logementIdDefault={editing.logement_id_default || ""}
          allLogIds={allLogs.map((l) => l.id)}
          onClose={() => setEditing(null)}
          onSubmit={async (payload, opts) => {
            const result = await upsert(payload, opts);
            handleSavedToast(result);
            if (result.ok || result.conflict) setEditing(null);
            return result;
          }}
        />
      )}

      {/* Phase 6.B PR #167c (ADR-101 Q-UI-2 E2) — modal édition min_stay_default */}
      {editingMinStay && (
        <MinStayDefaultEditModal
          logementId={editingMinStay.logementId}
          logementLabel={editingMinStay.logementLabel}
          onClose={() => setEditingMinStay(null)}
          onSaved={() => {
            // BPRcv2-1 / MPRcv2-3 — invalide hook configs (Vue 2 reflète valeur)
            refetchConfigs();
            // Note : globalInvalidate (CalendarPanel useDailyPricing) sera
            // déclenché au prochain mount calendar — acceptable V1 (cf. plan §6.5).
          }}
        />
      )}

      {/* Garde « Force resync » (2026-09-28) — le geste écrase des prix fixés
          à la main et rien n'en conserve la valeur d'avant. On réutilise la
          modale de confirmation générique (src/components/Modal.jsx, déjà
          porteuse de `children` + `okDisabled` depuis le chantier de l'avoir)
          plutôt que d'en créer une : PricingModeToggleModal n'est pas un
          composant de confirmation, c'est un formulaire de bascule PriceLabs. */}
      {forceResyncDemande &&
        (() => {
          // La fenêtre annoncée est celle sur laquelle le SERVEUR a compté —
          // pas une fenêtre recalculée ici, qui pourrait diverger (bord de
          // minuit, horizon changé). À défaut, repli local explicite.
          const debut =
            manualCountsWindow?.from || new Date().toISOString().slice(0, 10);
          const fin =
            manualCountsWindow?.to ||
            horizonFinIso(debut, HORIZON_FORCE_RESYNC_JOURS);
          const epingles = epinglesParLogement[forceResyncDemande] || {
            etat: "indisponible",
            nombre: null,
          };
          const desc = describeForceResync({
            logementId: forceResyncDemande,
            pricingMode:
              configsById.get(forceResyncDemande)?.pricing_mode || "manual",
            epinglesCount: epingles.nombre,
            epinglesLoaded: epingles.etat === "nombre",
            horizonDays:
              manualCountsWindow?.horizon_days || HORIZON_FORCE_RESYNC_JOURS,
            dateFrom: debut,
            dateTo: fin,
          });
          const enAttente = epingles.etat === "chargement";
          return (
            <Modal
              title={`Relire Beds24 pour ${forceResyncDemande}`}
              msg={
                desc.possible
                  ? "Avant de lancer, lis ce que ce geste écrase :"
                  : desc.raison
              }
              okLabel={
                enAttente
                  ? "Comptage des 📌…"
                  : desc.possible
                    ? "Relire et écraser les prix"
                    : "Indisponible"
              }
              okDisabled={forceResyncOkDisabled(desc)}
              onCancel={() => setForceResyncDemande(null)}
              onOk={() => lancerForceResync(forceResyncDemande)}
            >
              {desc.possible && (
                <ul
                  data-testid="force-resync-faits"
                  style={{
                    margin: 0,
                    paddingLeft: 18,
                    fontSize: 12.5,
                    lineHeight: 1.55,
                    color: "#444",
                  }}
                >
                  {desc.faits.map((f, i) => (
                    <li key={i} style={{ marginBottom: 6 }}>
                      {f}
                    </li>
                  ))}
                </ul>
              )}
              {!desc.possible && enAttente && (
                <div
                  data-testid="force-resync-comptage-en-cours"
                  style={{ fontSize: 12.5, color: "#777" }}
                >
                  Je compte les dates épinglées 📌 sur la période…
                </div>
              )}
            </Modal>
          );
        })()}

      {/* Phase 6.C PR #167d (ADR-102, D-2 + D-5) — modal bascule mode pricing */}
      {togglingConfig && (
        <PricingModeToggleModal
          existing={togglingConfig}
          manualPushSetCount={
            manualPushSetCountByLogement[togglingConfig.logement_id] ?? null
          }
          onClose={() => setTogglingConfig(null)}
          onSubmit={async (payload, opts) => {
            const result = await handleToggleModeSubmit(payload, opts);
            if (result?.ok || result?.conflict) {
              setTogglingConfig(null);
            }
            return result;
          }}
        />
      )}
    </div>
  );
}

// ── Sous-composants UI (inline, D1 validé Dimitri) ────────────────────

// R3 (ADR-256) — formate une date ISO nue en jour FR compact (sans heure).
function fmtRecoDay(iso) {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// R3 (ADR-256) — section « Divergences Beds24 ». Diagnostic LECTURE SEULE : elle
// LISTE les écarts entre la source de vérité Beds24 (daily_pricing.num_avail) et
// l'état de l'app (blocs FBZ + résas). Aucune action ici — le traitement des
// divergences relèvera d'un chantier ultérieur (au calendrier). Ne rien
// promettre côté copy (règle « textes d'aide = état actuel »).
function Beds24ReconciliationSection({ loading, error, data, allLogs }) {
  const counts = data?.counts || null;
  const senseATotal = counts?.senseA || 0;
  const senseBTotal = counts?.senseB || 0;
  const horizonTotal = counts?.horizon || 0;
  const neverPushedTotal = counts?.senseB_neverPushed || 0;
  const divergences = senseATotal + senseBTotal;

  const labelFor = (lid) => {
    const l = (allLogs || []).find((x) => x.id === lid);
    return l ? `${l.nom} (${l.prop})` : lid;
  };

  let badge;
  if (loading) {
    badge = <StatusPill tone="neutral">Analyse…</StatusPill>;
  } else if (error) {
    badge = <StatusPill tone="danger">Indisponible</StatusPill>;
  } else if (divergences === 0) {
    badge = (
      <StatusPill tone="success">✓ Miroir Beds24 — 0 divergence</StatusPill>
    );
  } else {
    badge = (
      <StatusPill tone="warning">
        ⚠ {divergences} divergence{divergences > 1 ? "s" : ""} détectée
        {divergences > 1 ? "s" : ""}
      </StatusPill>
    );
  }

  // Logements porteurs d'une divergence ACTIONNABLE (senseA ou senseB).
  const byLog = data?.byLogement || {};
  const logsWithDivergence = Object.keys(byLog).filter(
    (lid) =>
      (byLog[lid]?.senseA?.length || 0) > 0 ||
      (byLog[lid]?.senseB?.length || 0) > 0,
  );

  return (
    <Section
      // Force le remount quand l'état se stabilise : ouverte si divergence ou
      // erreur, repliée si miroir propre (badge vert suffit).
      key={data || error ? "recon-settled" : "recon-loading"}
      tint="ciel"
      icon={GitCompare}
      title="Divergences Beds24"
      subtitle="Écarts entre Beds24 (source de vérité) et l'état représenté dans l'app. Diagnostic en lecture seule."
      defaultOpen={divergences > 0 || !!error}
      headerRight={badge}
    >
      {loading && (
        <div style={{ fontSize: 12, color: DS.textMuted }}>
          Analyse des divergences en cours…
        </div>
      )}

      {!loading && error && <FicheAlert tone="danger">{error}</FicheAlert>}

      {!loading && !error && divergences === 0 && (
        <FicheAlert tone="success">
          Aucune divergence sur la fenêtre analysée : l'app reflète fidèlement
          les blocages Beds24.
        </FicheAlert>
      )}

      {!loading &&
        !error &&
        divergences > 0 &&
        logsWithDivergence.map((lid) => {
          const entry = byLog[lid];
          const senseA = entry.senseA || [];
          const senseB = entry.senseB || [];
          return (
            <div
              key={lid}
              style={{
                marginBottom: 14,
                paddingBottom: 12,
                borderBottom: `1px solid ${DS.border}`,
              }}
            >
              <div
                style={{
                  fontFamily: DS.fontDisplay,
                  fontWeight: 600,
                  fontSize: 13.5,
                  color: DS.marineDark,
                  marginBottom: 6,
                }}
              >
                {labelFor(lid)}
              </div>

              {senseA.length > 0 && (
                <div style={{ marginBottom: senseB.length > 0 ? 8 : 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: DS.warningText,
                      marginBottom: 3,
                    }}
                  >
                    Bloqué sur Beds24, non repris dans l'app
                  </div>
                  <ul style={reconListStyle}>
                    {senseA.map((s, i) => (
                      <li key={`a-${i}`} style={reconItemStyle}>
                        du {fmtRecoDay(s.start)} au {fmtRecoDay(s.end)} ·{" "}
                        {s.nights} nuit{s.nights > 1 ? "s" : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {senseB.length > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: DS.warningText,
                      marginBottom: 3,
                    }}
                  >
                    Bloc FBZ sans blocage Beds24 (périmé ?)
                  </div>
                  <ul style={reconListStyle}>
                    {senseB.map((s, i) => (
                      <li key={`b-${i}`} style={reconItemStyle}>
                        du {fmtRecoDay(s.start)} au {fmtRecoDay(s.end)} ·{" "}
                        {s.availableNights} nuit
                        {s.availableNights > 1 ? "s" : ""} disponible
                        {s.availableNights > 1 ? "s" : ""} côté Beds24
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}

      {/* Info discrète : fermetures d'horizon volontaires (non actionnables). */}
      {!loading && !error && horizonTotal > 0 && (
        <div
          style={{
            fontSize: 11.5,
            color: DS.textMuted,
            marginTop: 4,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Info size={13} strokeWidth={1.8} style={{ flex: "0 0 auto" }} />
          Fermetures d'horizon : {horizonTotal} segment
          {horizonTotal > 1 ? "s" : ""} (volontaires, non actionnables).
        </div>
      )}

      {/* Info discrète : blocs FBZ jamais poussés vers Beds24 (≠ périmé). */}
      {!loading && !error && neverPushedTotal > 0 && (
        <div
          style={{
            fontSize: 11.5,
            color: DS.textMuted,
            marginTop: 4,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Info size={13} strokeWidth={1.8} style={{ flex: "0 0 auto" }} />
          Blocs FBZ jamais poussés vers Beds24 : {neverPushedTotal} (non
          comptés comme divergence).
        </div>
      )}
    </Section>
  );
}

const reconListStyle = {
  margin: 0,
  paddingLeft: 18,
  listStyle: "disc",
};
const reconItemStyle = {
  fontSize: 12,
  color: DS.textSecondary,
  lineHeight: 1.55,
};

// BUG 62 — encart « Webhook temps réel ». Affiche le dernier event Inventory
// reçu (timestamp + type), la latence indicative de traitement, et un statut
// OK / muet selon l'âge de l'event (< 24 h = OK).
function WebhookStatusCard({ webhookStatus, fmtDate }) {
  // ADJ3 — deux lectures séparées : santé de la chaîne (ne se dégrade pas avec
  // le temps) + dernier changement de prix reçu (info neutre).
  const chain = evaluateWebhookChain(webhookStatus);
  const lastEvent = lastPriceEvent(webhookStatus);
  const isConnected = chain.kind === "connected";
  const isUnknown = chain.kind === "unknown";

  const badge = isConnected
    ? { tone: "success", Icon: CheckCircle2, label: "Connecté" }
    : isUnknown
      ? { tone: "neutral", Icon: HelpCircle, label: "Indisponible" }
      : { tone: "warning", Icon: AlertTriangle, label: "Jamais vu" };
  const BadgeIcon = badge.Icon;

  return (
    <Section
      tint="ciel"
      icon={Radio}
      title="Webhook temps réel"
      subtitle="Push Beds24 (≈ 3 s) : un prix inchangé depuis plusieurs jours est normal."
      headerRight={
        <span role="status" aria-label={`Statut webhook : ${badge.label}`}>
          <StatusPill tone={badge.tone}>
            <BadgeIcon size={11} strokeWidth={2} /> {badge.label}
          </StatusPill>
        </span>
      }
    >
      {isUnknown ? (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 9,
            padding: "12px 14px",
            background: DS.pageBg,
            color: DS.textSecondary,
            borderRadius: 10,
            fontSize: 12,
          }}
        >
          <HelpCircle size={15} strokeWidth={1.9} style={{ flex: "0 0 auto", marginTop: 1 }} />
          <span>
            Statut webhook indisponible (lecture serveur impossible). Vérifie le
            filet cron ci-dessus.
          </span>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
            gap: 10,
          }}
        >
          {/* Santé de la chaîne — neutre, n'affiche pas d'âge (ne dégrade pas). */}
          <Stat
            label="Chaîne webhook"
            value={isConnected ? "Connectée" : "Jamais vue"}
            color={isConnected ? DS.success : DS.warning}
          />
          {/* Dernier CHANGEMENT DE PRIX reçu — info événementielle neutre. */}
          <Stat
            label="Dernier changement de prix reçu"
            value={
              lastEvent.hasEvent
                ? fmtDate(lastEvent.lastEventAt)
                : "aucun encore"
            }
          />
          <Stat
            label="Reçu"
            value={
              lastEvent.hasEvent ? formatRelativeAge(lastEvent.ageMs) : "—"
            }
          />
          <Stat
            label="Latence traitement"
            value={
              lastEvent.processingMs != null
                ? `${lastEvent.processingMs} ms`
                : "—"
            }
            color={DS.marine}
          />
        </div>
      )}

      {chain.kind === "never" && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 9,
            marginTop: 10,
            padding: "8px 12px",
            background: DS.warningBg,
            color: DS.warningText,
            borderRadius: 8,
            borderLeft: `3px solid ${DS.warning}`,
            fontSize: 11,
          }}
        >
          <AlertTriangle size={14} strokeWidth={1.9} style={{ flex: "0 0 auto", marginTop: 1 }} />
          <span>
            Aucun event webhook n'a jamais été reçu. Si c'est inattendu, vérifie
            la config webhook côté Beds24. Le filet cron (15 min / 4 h) rattrape
            dans tous les cas.
          </span>
        </div>
      )}
    </Section>
  );
}

function Stat({ label, value, color = DS.textPrimary }) {
  return (
    <div
      style={{ background: DS.pageBg, borderRadius: 8, padding: "10px 12px", border: `0.5px solid ${DS.border}` }}
    >
      <div
        style={{
          fontSize: 10,
          color: DS.textMuted,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color, fontFamily: DS.fontDisplay }}>{value}</div>
    </div>
  );
}

// NB : ModeBadge + FreshnessCell ont été déplacés vers
// src/tabs/beds24/MappingRow.jsx (commit 8 PR4 NIT-1 extraction).
// OrphanRow contient sa propre copie de ModeBadge pour découplage
// (composants indépendants, pas d'import croisé).

const th = {
  padding: "8px 10px",
  textAlign: "left",
  color: DS.textSecondary,
  fontWeight: 700,
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: 0.4,
};
const td = { padding: "8px 10px", fontSize: 12 };

const btnSecondary = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  background: DS.card,
  color: DS.marine,
  border: `0.5px solid ${DS.borderHover}`,
  borderRadius: 7,
  padding: "5px 10px",
  fontSize: 11,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};
